import axios from 'axios';
import { updateError } from '../redux/slices/user';
import { dispatch } from '../redux/store';
// config
import { HOST_API, ADMIN_HOST_API } from '../config';
import { refreshAccessToken, accessTokenNeedsRefresh } from './authRefresh';
import { captureAuthRefreshStage } from './authSessionTelemetry';

// ===== Throttling / Retry Settings =====
const MAX_CONCURRENCY = 10;              // cap concurrent in‑flight requests
const REQUEST_TIMEOUT_MS = 10000;       // axios timeout per request (default; per-call overrideable)
const MAX_RETRIES = 2;                  // idempotent retries (GET/HEAD/OPTIONS) only
const BASE_BACKOFF_MS = 250;            // base backoff for jitter
// Largest Retry-After (delta-seconds) we will automatically sleep+retry on.
// Anything larger — or missing/invalid — surfaces a structured 429 to the
// caller immediately instead of blocking the UI for minutes. A `Retry-After:
// 2873` previously slept ~47.9 min per attempt (×2 retries ≈ 96-minute hang).
const MAX_RETRY_AFTER_SECONDS = 30;
// In-flight dedupe entries live until the request SETTLES (no age-based purge):
// a request doing long Retry-After waits is still live, so a late identical
// caller must join that same promise rather than stampede a second chain.

// Simple promise pool for concurrency limiting
export class RequestPool {
    constructor(limit) { this.limit = limit; this.active = 0; this.queue = []; this.highQueue = []; }

    // `priority: 'high'` (opt-in) waits in a second FIFO that is released before the normal one — still inside the
    // same concurrency cap. Callers that don't pass it behave exactly as before.
    async run(task, { priority } = {}) {
        if (this.active >= this.limit) {
            const lane = priority === 'high' ? this.highQueue : this.queue;
            await new Promise((resolve) => lane.push(resolve));
        }
        this.active += 1;
        try { return await task(); }
        finally { this.active -= 1; (this.highQueue.shift() || this.queue.shift())?.(); }
    }
}
const _pool = new RequestPool(MAX_CONCURRENCY);

// In-flight dedupe map: auth-scoped request key -> { promise }.
const _inflight = new Map();

// ===== Forced password change =====
// ForcedPasswordChangeMiddleware (dentalcrm/apps/users/middleware.py) answers
// 403 {"code": "password_change_required"} on every non-allowlisted view once
// must_change_password is set -- which is exactly what a super-admin
// temporary-credential reset sets. Until this existed, such a user logged in
// and then met 403s everywhere with nothing telling them why; the only way out
// was for someone to talk them through Profile -> Password by hand.
// Send them there instead. A hard navigation is fine: there is nothing else
// they are allowed to do until the password changes.
const FORCED_CHANGE_CODE = 'password_change_required';
const FORCED_CHANGE_PATH = '/dashboard/practice/profile?tab=Password';
let _forcedChangeRedirecting = false;

function _handleForcedPasswordChange(response) {
    if (response?.status !== 403) return;
    if (response?.data?.code !== FORCED_CHANGE_CODE) return;
    if (_forcedChangeRedirecting) return;
    if (typeof window === 'undefined' || !window.location) return;
    // Already on the page that can fix it — the change request itself is
    // allowlisted server-side, so do not bounce the user off mid-submit.
    if (window.location.pathname.includes('/practice/profile')) return;
    _forcedChangeRedirecting = true;
    window.location.assign(FORCED_CHANGE_PATH);
}

function _isIdempotent(method = 'GET') {
    const m = String(method).toUpperCase();
    return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
}

function _sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }
function _jitter(base, attempt) { return base * 2 ** attempt + Math.floor(Math.random() * base); }

function _makeDedupeKey(cfg, maxRetries = MAX_RETRIES) {
    const method = String(cfg.method || 'GET').toUpperCase();
    const url = cfg.url || '';
    const params = cfg.params ? JSON.stringify(cfg.params) : '';
    // Scope live entries to the request's auth identity. A SPA logout/login can
    // replace the token without reloading this module; without this component,
    // the new user could join the previous user's same-URL promise. The exact
    // value is already retained by the pending Axios config, is used only as a
    // transient Map-key component, is never logged/exported, and is released
    // when that in-flight entry settles. Avoid lossy hashing: a collision here
    // would reintroduce the cross-session response-isolation bug.
    const authScope = _readHeader(cfg.headers, 'Authorization') || '';
    // Public booking reads use owner credentials even without a CRM auth token.
    // Keep their in-flight responses isolated by the exact owner identity.
    const bookingOwner = Object.entries(cfg.headers || {}).find(([name]) => name.toLowerCase() === 'x-booking-session')?.[1] || '';
    // For GET we ignore body to improve cache hit; for POST you could hash a safe subset if desired
    return JSON.stringify([method, url, params, authScope, bookingOwner, maxRetries, cfg.responseType || 'json']);
}

function _resolveMaxRetries(value) {
    return Number.isInteger(value) && value >= 0 ? value : MAX_RETRIES;
}

// Read a header from either an AxiosHeaders instance (.get) or a plain object,
// tolerating case differences (mocks/tests often use lowercase plain objects).
function _readHeader(headers, name) {
    if (!headers) return undefined;
    if (typeof headers.get === 'function') {
        const viaGetter = headers.get(name);
        if (viaGetter != null) return viaGetter;
    }
    return headers[name] ?? headers[name.toLowerCase()];
}

// Strictly parse a Retry-After delta-seconds value. RFC 7231 delta-seconds is a
// non-negative integer; we deliberately reject anything else — blank, decimal,
// negative, HTTP-date, prefix junk like "30foo", NaN/Infinity — rather than
// using permissive parseInt (which would accept "30foo" as 30). Returns a
// non-negative safe integer, or null when the value is absent/invalid.
function _parseRetryAfterSeconds(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : null;
}

// The additive { seconds } metadata attached to every 429 result.
function _retryAfterMeta(resp) {
    return { seconds: _parseRetryAfterSeconds(_readHeader(resp?.headers, 'retry-after')) };
}

function _shapeSuccess(res) {
    return { ok: true, status: res.status, data: res.data, headers: res.headers };
}

// Single failure shaper used by BOTH the original caller and an in-flight
// dedupe joiner, so a 429's retryAfter metadata is identical on every path.
function _shapeFailure(err) {
    const resp = err?.response;
    const base = resp
        ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
        : { ok: false, status: 0, data: null, headers: {}, error: err };
    if (err?.retryAfterMeta) base.retryAfter = err.retryAfterMeta;
    return base;
}


axios.create({
    baseURL: HOST_API,
    timeout: REQUEST_TIMEOUT_MS,
});

axios.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
);

/**
 * Execute an Axios request with:
 *  - Concurrency limiting (pool)
 *  - In‑flight de‑duplication for idempotent requests
 *  - 429 handling that honors Retry‑After
 *  - Limited retries for transient/network/5xx on idempotent methods
 * Always returns a structured result { ok, status, data, headers, error }
 */
async function execRequest(config, maxRetries = MAX_RETRIES, { priority } = {}) {
    const cfg = { timeout: REQUEST_TIMEOUT_MS, ...config };

    const idempotent = _isIdempotent(cfg.method);
    // A caller-owned AbortSignal must never share an Axios promise with another
    // lifecycle. Aborting one component would otherwise cancel every deduped
    // caller waiting on the same URL.
    const key = idempotent && !cfg.signal ? _makeDedupeKey(cfg, maxRetries) : null;

    // Join an in-flight identical idempotent request. Entries live until the
    // request settles (no age purge), so a caller arriving even after a long
    // Retry-After wait joins the SAME live chain instead of starting a second.
    if (key && _inflight.has(key)) {
        try {
            const res = await _inflight.get(key).promise;
            return _shapeSuccess(res);
        } catch (err) {
            return _shapeFailure(err);
        }
    }

    let attempt = 0;
    const _do = () => _pool.run(() => axios(cfg), { priority });

    const attemptRequest = async () => {
        try {
            return await _do();
        } catch (err) {
            const resp = err?.response;
            const status = resp?.status;

            // 429 Too Many Requests. Compute Retry-After metadata for EVERY 429
            // (idempotent or not) and attach it to the error, so the structured
            // result carries { retryAfter: { seconds } } whether we retry, run
            // out of retry budget, or never retry. Only the retry DECISION is
            // gated by idempotency + a valid Retry-After within the cap.
            if (status === 429) {
                const meta = _retryAfterMeta(resp);
                err.retryAfterMeta = meta;
                const retryable =
                    idempotent &&
                    attempt < maxRetries &&
                    meta.seconds !== null &&
                    meta.seconds <= MAX_RETRY_AFTER_SECONDS;
                if (retryable) {
                    attempt += 1;
                    await _sleep(meta.seconds * 1000);
                    return attemptRequest();
                }
                throw err;
            }

            // Network errors / timeouts / retriable 5xx (except 501) for idempotent methods
            const retriable = (!resp || (status >= 500 && status !== 501)) || err?.code === 'ECONNABORTED';
            if (retriable && idempotent && attempt < maxRetries) {
                attempt += 1;
                await _sleep(_jitter(BASE_BACKOFF_MS, attempt));
                return attemptRequest();
            }
            throw err;
        }
    };

    const call = attemptRequest();

    if (key) _inflight.set(key, { promise: call });

    try {
        const res = await call;
        return _shapeSuccess(res);
    } catch (err) {
        return _shapeFailure(err);
    } finally {
        // Delete by promise identity only: never evict a newer replacement
        // entry (e.g. a bustCache-installed fresh chain) registered under the
        // same key after this call started.
        if (key && _inflight.get(key)?.promise === call) _inflight.delete(key);
    }
}

// A blocked-storage context (Edge/Chrome with site data denied, embedded
// third-party frame) throws SecurityError on a bare localStorage read and takes
// down app boot. Guarded inline rather than via localStorageGetItem: that helper
// probes availability with a setItem+removeItem on every call, and this runs on
// EVERY request through buildAuthHeader(). Same guard pattern as hooks/useDemoMode.js.
const accessToken = () => {
    try { return localStorage.getItem('accessToken'); } catch { return null; }
};

function buildAuthHeader() {
    const token = accessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// Refresh BEFORE the request when the stored access token is expired or about
// to be. Production logs showed every screen producing 401s on the first
// mutation after the 60-minute access token lapsed: the request went out
// stale, was rejected, refreshed and retried. That retry still exists below
// as the fallback for revocation and clock skew; this just stops the expected
// case from costing a failed round trip and a logged 401. A failed
// pre-refresh is deliberately swallowed: the request goes out with the
// stored token and the 401 path decides, exactly as before.
function ensureFreshAccessToken(endpoint) {
    // Best-effort by construction: nothing in here — the expiry check, the
    // refresh, the breadcrumb — may stop the request. Whatever fails, the
    // request goes out with the stored token and the 401 path decides.
    //
    // Deliberately NOT an async function: when the token is fine (almost
    // always) this returns null synchronously and callApi dispatches on the
    // same tick as before, which the in-flight dedupe relies on. A promise is
    // returned, and awaited by the caller, only when a refresh is needed.
    try {
        const token = accessToken();
        if (!token || !accessTokenNeedsRefresh(token)) return null;
        captureAuthRefreshStage('pre_refresh_triggered', { endpoint });
        return refreshAccessToken()
            .then(() => captureAuthRefreshStage('pre_refresh_succeeded', { endpoint }))
            .catch((err) => captureAuthRefreshStage('pre_refresh_failed', { endpoint, reason: err?.reason || err?.message }));
    } catch {
        return null;
    }
}

function refreshFailureResponse(err) {
    const resp = err?.response;
    return resp
        ? { ok: false, status: resp.status, data: resp.data, headers: resp.headers, error: err }
        : { ok: false, status: 0, data: null, headers: {}, error: err };
}

// During the backend-first Cal.com rollout an older RESTQL serializer rejects
// this optional read summary. Retry only that exact schema mismatch; never
// remove filters, suppress another validation error, or retry a mutation.
function calcomReadFallbackUri(uri, method, response) {
    if (String(method).toUpperCase() !== 'GET' || response?.status !== 400) return null;
    const error = Array.isArray(response.data) && response.data.length === 1
        ? response.data[0] : response.data?.detail;
    if (error !== '`calcom` field is not found') return null;
    const path = uri.split('?')[0];
    if (!/^\/api\/v1\/practices\/\d+\/(?:leads(?:\/(?:search|\d+(?:\/appointments(?:\/\d+)?)?))?|lead-treatments-flat)\/?$/.test(path)) return null;
    const match = uri.match(/([?&]fields=)([^&]*)/);
    if (!match) return null;
    let fields;
    try { fields = decodeURIComponent(match[2]); } catch { return null; }
    // The shared projections contain plain identifiers/braces, not aliases or
    // field arguments. Fail closed on a different RESTQL grammar.
    if (!/^[A-Za-z0-9_{},\s]+$/.test(fields) || !/[{,]\s*calcom\s*[,}]/.test(fields)) return null;
    const fallback = fields.replace(/([{,])\s*calcom\s*(?=[,}])/g, '$1')
        .replace(/,\s*,/g, ',').replace(/{\s*,/g, '{').replace(/,\s*}/g, '}');
    if (fallback === fields || /{\s*}/.test(fallback)) return null;
    return uri.replace(match[0], `${match[1]}${encodeURIComponent(fallback)}`);
}

/**
 * callApi
 * - Adds Authorization header unless `isUnAuthorized`
 * - Uses execRequest() which applies throttling/dedupe/retries
 * - On 401, performs a single token refresh, then retries once
 * - Optional `options` supports:
 *   - `responseType`: opt-in blob transport for authenticated downloads
 *   - `timeout`: per-call timeout for long-running requests
 *   - `maxRetries`: caller-owned retry limit for idempotent requests (default 2)
 *   - `signal`: AbortSignal; signal-bearing requests are never in-flight deduped
 *   - `priority`: 'high' queues the request ahead of normal ones in the concurrency pool (opt-in; default unchanged)
 *   - `suppressServerErrorDispatch`: caller owns 500/503 presentation (the
 *     structured response is unchanged; default global dispatch stays on)
 *   - `headers`: additional request headers; auth/content type remain managed here
 */
export async function callApi(uri, method = 'GET', data = null, isUnAuthorized = false, options = {}) {
    // Block requests with undefined/null in ANY path segment — not just practiceId.
    // Frontend code can pass undefined IDs via template strings (e.g. `/leads/${leadId}/`)
    // when state hasn't hydrated yet. These hit the backend as literal "undefined" or "null"
    // strings, causing ValueError: Field 'id' expected a number but got 'undefined'.
    if (uri && (/\/(?:undefined|null)(?:\/|$)/.test(uri))) {
        console.warn('[apiCall] Blocked request with invalid path parameter:', uri);
        return { ok: false, status: 0, data: null, headers: {}, error: 'Invalid request parameter' };
    }
    // Block any request with /undefined/ or /null/ in path segments (e.g. /leads/undefined/treatments/)
    const uriPath = uri?.split('?')[0] || '';
    if (/\/(undefined|null)\//.test(uriPath) || uriPath.endsWith('/undefined') || uriPath.endsWith('/null')) {
        console.warn('[apiCall] Blocked request with undefined/null path segment:', uri);
        return { ok: false, status: 0, data: null, headers: {}, error: 'Invalid path parameter' };
    }
    const isFormData = data instanceof FormData;
    const config = {
        method,
        url: `${HOST_API}${uri}`,
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
        data,
    };
    if (options.responseType === 'blob') config.responseType = 'blob';
    Object.entries(options.headers || {}).forEach(([name, value]) => {
        if (!['authorization', 'content-type'].includes(name.toLowerCase())) {
            config.headers[name] = value;
        }
    });
    if (options.timeout) {
        config.timeout = options.timeout;
    }
    if (options.signal) {
        config.signal = options.signal;
    }
    if (!isUnAuthorized) {
        const preRefresh = ensureFreshAccessToken(uriPath);
        if (preRefresh) await preRefresh;
        Object.assign(config.headers, buildAuthHeader());
        if (!config.headers.Authorization) {
            return { ok: false, status: 401, data: null, headers: {}, error: 'Missing access token' };
        }
    }

    // Opt-in cache bust: evict any in-flight dedupe entry for this URL before the
    // first execRequest so a post-mutation GET starts a fresh request instead of
    // joining an older in-flight chain.  callApi owns this decision —
    // execRequest is not aware of bustCache at all.
    // Map.delete on a missing key is a safe no-op so no guard needed.
    const maxRetries = _resolveMaxRetries(options.maxRetries);
    if (options.bustCache) {
        _inflight.delete(_makeDedupeKey(config, maxRetries));
    }

    // First attempt
    const execOptions = options.priority ? { priority: options.priority } : undefined;
    let response = await execRequest(config, maxRetries, execOptions);

    // 500/503: notify but still return structured response
    if ((response.status === 500 || response.status === 503) && !options.suppressServerErrorDispatch) {
        const endpoint = uri?.split('?')[0] || 'unknown';
        dispatch(updateError({ message: `Something went wrong! (${endpoint})`, success: false, status: response.status }));
    }

    // 401: try token refresh (only when authorized path)
    if (response.status === 401 && !isUnAuthorized) {
        captureAuthRefreshStage('401_refresh_retry', { endpoint: uriPath, method });
        try {
            const newAccessToken = await refreshAccessToken();
            if (!newAccessToken) return { ok: false, status: 401, data: null, headers: {}, error: 'Missing refreshed access token' };
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            response = await execRequest(config, maxRetries, execOptions);
            if (response.status === 401) captureAuthRefreshStage('401_after_retry', { endpoint: uriPath, method });
        } catch (err) {
            captureAuthRefreshStage('401_refresh_failed', { endpoint: uriPath, method, reason: err?.reason || err?.message });
            return refreshFailureResponse(err);
        }
    }

    const fallbackUri = calcomReadFallbackUri(uri, method, response);
    if (fallbackUri) {
        // Authentication (including a refreshed token), filters, abort signal
        // and timeout stay identical. This is one compatibility GET, no loop.
        response = await execRequest({ ...config, url: `${HOST_API}${fallbackUri}` }, 0, execOptions);
    }

    // Last, so it inspects the FINAL response: the Cal.com fallback above can
    // replace it, and a 403 from that retry has to be caught too.
    _handleForcedPasswordChange(response);

    return response;
}

/**
 * callAdminApi
 * - Always sends Authorization header
 * - Uses execRequest() which applies throttling/dedupe/retries
 * - On 401, performs a token refresh then retries once
 */
export async function callAdminApi(uri, method = 'GET', data = null, options = {}) {
    const isFormData = data instanceof FormData;
    const config = {
        method,
        url: `${ADMIN_HOST_API}${uri}`,
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
        data,
    };
    if (options && Number.isFinite(options.timeout)) {
        // Per-call timeout override. The default REQUEST_TIMEOUT_MS (10s)
        // is too tight for endpoints that do Celery broadcast RPCs +
        // multiple COUNT(*) queries on a cold cache (e.g. the
        // tasks-migration diagnostics view). Without this knob those
        // calls timed out and returned status=0 even though the BE
        // eventually finished and returned 200.
        config.timeout = options.timeout;
    }
    // Per-call extra headers (e.g. the Idempotency-Key the temporary-credential
    // reset requires). Applied BEFORE buildAuthHeader so a caller can never
    // overwrite Authorization from here.
    if (options && options.headers) {
        Object.assign(config.headers, options.headers);
    }
    const preRefresh = ensureFreshAccessToken(uri?.split('?')[0] || '');
    if (preRefresh) await preRefresh;
    Object.assign(config.headers, buildAuthHeader());
    if (!config.headers.Authorization) {
        return { ok: false, status: 401, data: null, headers: {}, error: 'Missing access token' };
    }

    let response = await execRequest(config);

    if (response.status === 500 || response.status === 503) {
        const endpoint = uri?.split('?')[0] || 'unknown';
        dispatch(updateError({ message: `Something went wrong! (${endpoint})`, success: false, status: response.status }));
    }

    if (response.status === 401) {
        try {
            const newAccessToken = await refreshAccessToken();
            if (!newAccessToken) return { ok: false, status: 401, data: null, headers: {}, error: 'Missing refreshed access token' };
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            response = await execRequest(config);
        } catch (err) {
            return refreshFailureResponse(err);
        }
    }

    _handleForcedPasswordChange(response);

    return response;
}
